import { memo, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Flame, CheckCircle2, Eye, Clock, AlertTriangle, Search,
} from 'lucide-react';
import { toast } from 'sonner';
import { PageHeader } from '@/components/shared/PageHeader';
import { EmptyState } from '@/components/shared/EmptyState';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAuthStore } from '@/stores/authStore';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';
import type { WorkflowEscalation } from '@/types/api';

const STATUS_TABS = [
  { value: 'open', label: 'Open' },
  { value: 'acknowledged', label: 'Acknowledged' },
  { value: 'all', label: 'All' },
] as const;

const STATUS_STYLES: Record<string, { badge: string; dot: string }> = {
  open: { badge: 'bg-compliance-critical/15 text-compliance-critical border-compliance-critical/30', dot: 'bg-compliance-critical' },
  acknowledged: { badge: 'bg-compliance-medium/15 text-compliance-medium border-compliance-medium/30', dot: 'bg-compliance-medium' },
  resolved: { badge: 'bg-compliance-low/15 text-compliance-low border-compliance-low/30', dot: 'bg-compliance-low' },
  dismissed: { badge: 'bg-muted text-muted-foreground border-border', dot: 'bg-muted-foreground' },
};

function formatRelativeTime(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const minutes = Math.floor(diff / 60_000);
  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

/** Replace any raw ISO timestamps embedded in text with readable dates */
function cleanISODates(text: string): string {
  return text.replace(/\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?Z?/g, (m) => formatDate(m));
}

export function EscalationsPage() {
  const user = useAuthStore((s) => s.user);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const statusFilter = searchParams.get('status') ?? 'open';
  const searchQuery = searchParams.get('q') ?? '';
  const visibleCount = parseInt(searchParams.get('show') ?? '20', 10);

  const setStatusFilter = useCallback((v: string) => {
    setSearchParams((prev) => { prev.set('status', v); prev.delete('show'); return prev; });
  }, [setSearchParams]);

  const setSearchQuery = useCallback((v: string) => {
    setSearchParams((prev) => { if (v) prev.set('q', v); else prev.delete('q'); return prev; });
  }, [setSearchParams]);

  const loadMore = useCallback(() => {
    setSearchParams((prev) => { prev.set('show', String(visibleCount + 20)); return prev; });
  }, [setSearchParams, visibleCount]);

  const { data, isLoading } = useQuery({
    queryKey: ['escalations-all'],
    queryFn: () => api.escalations.list(),
    enabled: !!user,
    staleTime: 30_000,
    refetchInterval: 60_000,
  });

  const { data: myData } = useQuery({
    queryKey: ['escalations-mine'],
    queryFn: () => api.escalations.mine(),
    enabled: !!user,
    staleTime: 30_000,
    refetchInterval: 60_000,
  });

  const invalidateAll = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['escalations-all'] });
    queryClient.invalidateQueries({ queryKey: ['escalations-mine'] });
    queryClient.invalidateQueries({ queryKey: ['workflow-dashboard'] });
    queryClient.invalidateQueries({ queryKey: ['nav-escalation-count'] });
  }, [queryClient]);

  const acknowledgeMutation = useMutation({
    mutationFn: (eventId: string) => api.escalations.acknowledge(eventId),
    onMutate: async (eventId) => {
      await queryClient.cancelQueries({ queryKey: ['escalations-all'] });
      const prev = queryClient.getQueryData<{ escalations: WorkflowEscalation[] }>(['escalations-all']);
      queryClient.setQueryData(['escalations-all'], (old: { escalations: WorkflowEscalation[] } | undefined) => ({
        escalations: (old?.escalations ?? []).map((e) =>
          e.eventId === eventId ? { ...e, status: 'acknowledged' as const, acknowledgedAt: new Date().toISOString() } : e,
        ),
      }));
      return { prev };
    },
    onSuccess: () => toast.success('Escalation acknowledged'),
    onError: (_err, _id, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(['escalations-all'], ctx.prev);
      toast.error('Failed to acknowledge');
    },
    onSettled: invalidateAll,
  });

  const resolveMutation = useMutation({
    mutationFn: (eventId: string) => api.escalations.resolve(eventId),
    onMutate: async (eventId) => {
      await queryClient.cancelQueries({ queryKey: ['escalations-all'] });
      const prev = queryClient.getQueryData<{ escalations: WorkflowEscalation[] }>(['escalations-all']);
      queryClient.setQueryData(['escalations-all'], (old: { escalations: WorkflowEscalation[] } | undefined) => ({
        escalations: (old?.escalations ?? []).map((e) =>
          e.eventId === eventId ? { ...e, status: 'resolved' as const, resolvedAt: new Date().toISOString() } : e,
        ),
      }));
      return { prev };
    },
    onSuccess: () => toast.success('Escalation resolved'),
    onError: (_err, _id, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(['escalations-all'], ctx.prev);
      toast.error('Failed to resolve');
    },
    onSettled: invalidateAll,
  });

  const allEscalations = data?.escalations ?? [];
  const myEscalations = myData?.escalations ?? [];
  const myEventIds = new Set(myEscalations.map((e) => e.eventId));

  const filtered = allEscalations.filter((e) => {
    if (statusFilter === 'open' && e.status !== 'open') return false;
    if (statusFilter === 'acknowledged' && e.status !== 'acknowledged') return false;
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      return e.reason.toLowerCase().includes(q) || e.eventId.toLowerCase().includes(q);
    }
    return true;
  });

  const openCount = allEscalations.filter((e) => e.status === 'open').length;
  const ackCount = allEscalations.filter((e) => e.status === 'acknowledged').length;

  const handleAcknowledge = useCallback((id: string) => acknowledgeMutation.mutate(id), [acknowledgeMutation]);
  const handleResolve = useCallback((id: string) => resolveMutation.mutate(id), [resolveMutation]);
  const handleNavigateProject = useCallback((id: string) => navigate(`/projects/${id}`), [navigate]);

  return (
    <div className="flex h-full flex-col gap-6 overflow-y-auto p-6">
      <PageHeader
        title="Escalations"
        subtitle="Active compliance escalations requiring attention"
        actions={
          <Button variant="outline" size="sm" onClick={() => navigate('/admin/escalation-rules')}>
            Manage Rules
          </Button>
        }
      />

      {/* Summary stats */}
      <div className="flex items-center gap-6 text-sm">
        <div className="flex items-center gap-2">
          <span className={cn('h-2 w-2 rounded-full', openCount > 0 ? 'bg-compliance-critical animate-pulse' : 'bg-muted-foreground')} />
          <span className="text-muted-foreground"><span className="font-semibold text-foreground">{openCount}</span> open</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="h-2 w-2 rounded-full bg-compliance-medium" />
          <span className="text-muted-foreground"><span className="font-semibold text-foreground">{ackCount}</span> acknowledged</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="h-2 w-2 rounded-full bg-teal" />
          <span className="text-muted-foreground"><span className="font-semibold text-foreground">{myEventIds.size}</span> assigned to you</span>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <Tabs value={statusFilter} onValueChange={(v) => setStatusFilter(v as string)}>
          <TabsList variant="line">
            {STATUS_TABS.map((tab) => (
              <TabsTrigger key={tab.value} value={tab.value}>
                {tab.label}
                {tab.value === 'open' && openCount > 0 && (
                  <span className="ml-1.5 rounded-full bg-compliance-critical/20 px-1.5 py-0.5 text-[10px] font-semibold text-compliance-critical tabular-nums">{openCount}</span>
                )}
                {tab.value === 'acknowledged' && ackCount > 0 && (
                  <span className="ml-1.5 rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-semibold tabular-nums">{ackCount}</span>
                )}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>

        <div className="relative flex-1 max-w-xs ml-auto">
          <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search escalations..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="h-7 pl-8 text-sm"
          />
        </div>
      </div>

      {/* Escalation list */}
      {isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 6 }, (_, i) => (
            <div key={i} className="h-20 animate-pulse rounded-lg bg-card" />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={statusFilter === 'acknowledged' ? Eye : Flame}
          title={statusFilter === 'open' ? 'No open escalations' : statusFilter === 'acknowledged' ? 'No acknowledged escalations' : 'No escalations found'}
          description={statusFilter === 'open' ? 'All compliance tasks are on track.' : statusFilter === 'acknowledged' ? 'No escalations have been acknowledged yet.' : 'Try adjusting your filters.'}
        />
      ) : (
        <div className="space-y-2">
          {filtered.slice(0, visibleCount).map((esc) => (
            <EscalationCard
              key={esc.eventId}
              escalation={esc}
              isMine={myEventIds.has(esc.eventId)}
              onAcknowledge={handleAcknowledge}
              onResolve={handleResolve}
              onNavigateProject={handleNavigateProject}
            />
          ))}
          {visibleCount < filtered.length && (
            <div className="flex items-center justify-between pt-2">
              <p className="text-sm text-muted-foreground">Showing {visibleCount} of {filtered.length}</p>
              <Button variant="outline" size="sm" onClick={loadMore}>Load More</Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const EscalationCard = memo(function EscalationCard({
  escalation,
  isMine,
  onAcknowledge,
  onResolve,
  onNavigateProject,
}: {
  readonly escalation: WorkflowEscalation;
  readonly isMine: boolean;
  readonly onAcknowledge: (id: string) => void;
  readonly onResolve: (id: string) => void;
  readonly onNavigateProject: (id: string) => void;
}) {
  const style = STATUS_STYLES[escalation.status] ?? STATUS_STYLES.open;

  return (
    <Card className={cn(
      escalation.status === 'open' && 'border-compliance-critical/30',
      isMine && escalation.status === 'open' && 'ring-1 ring-compliance-critical/20',
    )}>
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <div className="mt-1 shrink-0">
            {escalation.status === 'open' ? (
              <AlertTriangle className="h-4 w-4 text-compliance-critical" />
            ) : escalation.status === 'acknowledged' ? (
              <Eye className="h-4 w-4 text-compliance-medium" />
            ) : (
              <CheckCircle2 className="h-4 w-4 text-compliance-low" />
            )}
          </div>

          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <p className="text-sm font-medium text-foreground">{cleanISODates(escalation.reason)}</p>
              {isMine && (
                <Badge variant="outline" className="text-[9px] bg-teal/10 text-teal-light border-teal/30">Assigned to you</Badge>
              )}
            </div>
            <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
              <Badge variant="outline" className={cn('text-[10px] capitalize', style.badge)}>{escalation.status}</Badge>
              <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{formatRelativeTime(escalation.createdAt)}</span>
              {escalation.taskId && (
                <span className="text-[10px]">Task: {escalation.taskTitle ?? escalation.taskId.slice(0, 8)}</span>
              )}
              {escalation.projectId && (
                <span className="text-[10px]">Project: {escalation.projectTitle ?? escalation.projectId.slice(0, 8)}</span>
              )}
              {escalation.acknowledgedAt && <span>Ack: {formatRelativeTime(escalation.acknowledgedAt)}</span>}
              {escalation.resolvedAt && <span>Resolved: {formatRelativeTime(escalation.resolvedAt)}</span>}
            </div>
          </div>

          <div className="flex shrink-0 items-center gap-2">
            {escalation.projectId && (
              <Button variant="ghost" size="sm" className="h-auto p-1.5 text-xs" onClick={() => onNavigateProject(escalation.projectId!)} aria-label="View related project">
                View
              </Button>
            )}
            {escalation.status === 'open' && (
              <Button variant="outline" size="sm" className="h-auto p-1.5 text-xs" onClick={() => onAcknowledge(escalation.eventId)} aria-label="Acknowledge escalation">
                <Eye className="mr-1 h-3 w-3" />Acknowledge
              </Button>
            )}
            {(escalation.status === 'open' || escalation.status === 'acknowledged') && (
              <Button size="sm" className="h-auto bg-compliance-low p-1.5 text-xs text-white hover:bg-compliance-low/90" onClick={() => onResolve(escalation.eventId)} aria-label="Resolve escalation">
                <CheckCircle2 className="mr-1 h-3 w-3" />Resolve
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
});
